<template>
  <div
    class="project-switcher"
    :class="{ 'project-switcher--collapsed': collapsed }"
  >
    <HLDropdown
      id="project-switcher-dropdown"
      v-model:show="dropdownOpen"
      :options="dropdownOptions"
      :show-search="false"
      placement="right-start"
      :width="320"
      max-height="300px"
      trigger="click"
      :highlight-selection="false"
      @select="handleSelect"
    >
      <!-- Trigger -->
      <template #trigger>
        <button
          class="project-switcher-trigger"
          :aria-label="`Switch project — ${projectData?.name || 'Select'}`"
        >
          <div
            v-if="!projectData?.id"
            class="project-switcher-empty-icon"
          >
            <HLIcon :size="18">
              <MaterialFolderOpenIcon />
            </HLIcon>
          </div>
          <HLAvatar
            v-else
            :id="`project-avatar-${projectData?.id}`"
            :name="projectData?.name || '?'"
            :round="false"
            size="sm"
            preferred-initials-bg-color="var(--secondary-100)"
            class="project-switcher-avatar"
          />
          <template v-if="!collapsed">
            <div class="project-switcher-info">
              <HLText
                v-if="projectData?.team?.name"
                id="project-switcher-team"
                size="3xs"
                weight="medium"
                html-tag="span"
                class="project-switcher-team"
              >
                {{ projectData?.team?.name }}
              </HLText>
              <span class="project-switcher-name-row">
                <HLIcon
                  v-if="isCurrentProjectPrivate"
                  :size="12"
                  class="project-switcher-lock"
                  aria-label="Private project"
                >
                  <Lock01Icon />
                </HLIcon>
                <HLText
                  id="project-switcher-name"
                  size="lg"
                  weight="medium"
                  html-tag="span"
                  class="project-switcher-name"
                >
                  {{ projectData?.name || 'Select Project' }}
                </HLText>
              </span>
            </div>
            <HLIcon
              :size="20"
              class="project-switcher-chevron"
            >
              <ChevronRightIcon />
            </HLIcon>
          </template>
        </button>
      </template>

      <!-- Header: Back link + search -->
      <template #header>
        <div class="project-dropdown-header">
          <HLButton
            v-if="projectData?.id"
            id="project-dropdown-back-btn"
            variant="text"
            color="primary"
            size="xs"
            @click="handleSelect('back-to-projects')"
          >
            <template #iconLeft>
              <HLIcon :size="16">
                <ChevronLeftIcon />
              </HLIcon>
            </template>
            Back to All Projects
          </HLButton>
          <HLInput
            id="project-search-input"
            ref="searchInputRef"
            v-model:model-value="searchQuery"
            placeholder="Search projects..."
            size="xs"
            clearable
            class="pt-2"
          >
            <template #prefix>
              <HLIcon :size="16">
                <SearchSmIcon />
              </HLIcon>
            </template>
          </HLInput>
        </div>
      </template>

      <!-- Option rows -->
      <template #option-renderer="{ option }">
        <div
          class="project-option px-4"
          :class="{ 'project-option--active': option.key === projectData?.id }"
          @click="handleSelect(String(option.key))"
        >
          <HLAvatar
            :id="`project-option-avatar-${option.key}`"
            :name="option.label"
            :round="false"
            size="sm"
            :preferred-initials-bg-color="String(option.avatarColor)"
            class="project-option-avatar"
          />
          <div class="project-option-body">
            <HLText
              :id="`project-option-name-${option.key}`"
              size="md"
              weight="medium"
              html-tag="span"
              class="project-option-name"
            >
              {{ option.label }}
            </HLText>
            <HLEllipsis
              v-if="option.description"
              :id="`project-option-desc-${option.key}`"
              :line-clamp="1"
              :tooltip="false"
              class="project-option-desc"
            >
              <HLText
                :id="`project-option-desc-text-${option.key}`"
                size="2xs"
                weight="regular"
                html-tag="span"
              >
                {{ option.description }}
              </HLText>
            </HLEllipsis>
          </div>
          <div class="project-option-meta">
            <HLIcon
              v-if="option.isPrivate"
              :size="12"
              class="project-option-lock"
              aria-label="Private project"
            >
              <Lock01Icon />
            </HLIcon>
          </div>
        </div>
      </template>

      <!-- Footer -->
      <template #footer>
        <div class="project-dropdown-footer">
          <HLButton
            id="project-manage-btn"
            variant="secondary"
            color="primary"
            size="xs"
            full-width
            @click="handleSelect('manage-projects')"
          >
            <template #iconLeft>
              <HLIcon :size="14">
                <component :is="canManageProjects ? Settings01Icon : null" />
              </HLIcon>
            </template>
            {{ canManageProjects ? 'Manage Projects' : 'View All Projects' }}
          </HLButton>
        </div>
      </template>
    </HLDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { HLDropdown, HLAvatar, HLText, HLIcon, HLEllipsis, HLButton, HLInput } from '@platform-ui/highrise'
import { Settings01Icon, SearchSmIcon, ChevronRightIcon, ChevronLeftIcon, Lock01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { MaterialFolderOpenIcon } from '@gohighlevel/ghl-icons/24/material/rounded'
import { useProjectStore } from '@/stores/project'
import { useAuthStore } from '@/stores/auth'
import { getProjectAvatarColor } from '@/utils/badgeHelpers'
import { http } from '@/utils/httpClient'
import { useProjectDataStore } from '@/stores/projectData'
import { canAccessProject } from '@/constants/teams'
import { usePermissions } from '@/composables/usePermissions'


defineProps<{
  collapsed: boolean
}>()

const router = useRouter()
const route = useRoute()
const { getProjectData } = useProjectDataStore()
const projectData = ref<any>({})
const projectStore = useProjectStore()
const authStore = useAuthStore()
const { can } = usePermissions()
const canManageProjects = computed(() => can.editProjects() || can.createProjects())

const searchQuery = ref('')
const dropdownOpen = ref(false)
const searchInputRef = ref<any>(null)



const projects = ref<any[]>([])

const isCurrentProjectPrivate = computed(
  () => (projectData.value?.visibility || '').toLowerCase() === 'private',
)

const dropdownOptions = computed(() => {
  const raw = projects.value
  const list = Array.isArray(raw) ? raw : []
  const q = searchQuery.value.toLowerCase().trim()
  const userProfile = authStore.userProfile
  const currentId = projectData.value?.id
  const visible = list
    .filter((p) => canAccessProject(p, userProfile))
    .filter((p) => p.id === currentId || (p.status || '').toLowerCase() === 'active')
    .filter((p) => !q || p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q))
  visible.sort((a, b) => {
    if (a.id === currentId) return -1
    if (b.id === currentId) return 1
    return 0
  })
  return visible.map((p, i) => ({
    key: p.id,
    label: p.name,
    description: p.description || p.team?.name || '',
    infoText: p.status || '',
    avatarColor: getProjectAvatarColor(i),
    isPrivate: (p.visibility || '').toLowerCase() === 'private',
  }))
})


function handleSelect(key: string) {
  dropdownOpen.value = false
  searchQuery.value = ''
  if (key === 'manage-projects' || key === 'back-to-projects') {
    router.push('/projects')
    return
  }
  router.push(`/projects/${key}/overview`)
}

onBeforeMount(async () => {
  // Slim list: only request fields the switcher actually renders. `visibility`
  // and `members` are required so we can hide private projects from non-members
  // and render the lock badge for the rest (mirrors the Projects page rules).
  const fields = ['id', 'name', 'description', 'status', 'team', 'visibility', 'members'].join(',')
  projects.value = await http.get(`/projects/all?fields=${fields}`)
  if (!projectStore.projects.length) projectStore.projects = projects.value
  const pid = route.params.projectId as string | undefined
  // Clear current-project display on global pages (e.g. /dashboard) so the
  // trigger renders the "Select Project" empty state instead of leaking a
  // stale cached project.
  projectData.value = pid ? await getProjectData(pid) : {}
})

watch(
  () => route.params.projectId,
  async (projectId) => {
    projectData.value = projectId ? await getProjectData(projectId as string) : {}
  },
)

watch(dropdownOpen, async (open) => {
  if (!open) return
  await nextTick()
  const el = searchInputRef.value
  const focus = el?.focus ?? el?.$el?.querySelector?.('input')?.focus?.bind(el?.$el?.querySelector?.('input'))
  focus?.()
})

</script>
<style>
/* Dropdown theme */
.hr-dropdown-menu {
  background: var(--neutral-50);
  padding: 4px;
  .hr-popover-arrow-wrapper .hr-popover-arrow{
    background-color: var(--neutral-50) !important;
  }
}

</style>
<style scoped>
.project-switcher {
  padding: 6px;
  .hr-dropdown__container{
    width: 100%;
  }
}

.project-switcher--collapsed {
  padding: 4px 10px;
  display: flex;
  justify-content: center;
  .hr-dropdown__container{
    width: fit-content;
  }
}

.project-switcher-trigger {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 10px 12px 10px 14px;
  border: 1px solid color-mix(in srgb, var(--secondary-500) 18%, transparent);
  border-radius: 10px;
  background: var(--secondary-25);
  box-shadow: var(--kaizen-shadow-sm);
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.project-switcher-trigger::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--primary-500);
  opacity: 0.85;
}

.project-switcher-trigger:hover {
  border-color: color-mix(in srgb, var(--primary-500) 35%, transparent);
  background: color-mix(in srgb, var(--primary-50) 60%, var(--secondary-25));
  box-shadow: var(--kaizen-shadow-md);
  transform: translateY(-1px);
}

.project-switcher--collapsed .project-switcher-trigger {
  width: auto;
  max-width: 100%;
  padding: 6px;
  justify-content: center;
}

.project-switcher--collapsed .project-switcher-trigger::before {
  display: none;
}

.project-switcher-avatar,
.project-option-avatar {
  flex-shrink: 0;
}

/* Generic project icon shown when no project is selected (e.g. /dashboard).
   Matches HLAvatar size="sm" footprint so the trigger height stays stable. */
.project-switcher-empty-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--primary-500) 12%, var(--base-white));
  color: var(--primary-600);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--primary-500) 18%, transparent);
}


.project-switcher-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  display: flex;
  flex-direction: column;
}

.project-switcher-team {
  color: var(--primary-700);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.2;
}

.project-switcher-name {
  color: var(--neutral-900);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.project-switcher-chevron {
  flex-shrink: 0;
  color: var(--primary-600);
  align-self: center;
  transition: transform 0.18s ease;
}

.project-switcher-trigger:hover .project-switcher-chevron {
  transform: translateX(2px);
}



:deep(.hr-dropdown-option) {
  background: transparent !important;
  padding: 0 !important;
  border-radius: 6px;
}

:deep(.hr-dropdown-option:hover),
:deep(.hr-dropdown__menu-item:hover),
:deep(.hr-dropdown__menu-item--interactive:hover) {
  background: var(--neutral-100) !important;
}

:deep(.hr-dropdown-option:hover .project-option),
:deep(.hr-dropdown__menu-item:hover .project-option) {
  background: var(--neutral-100);
}

:deep(.hr-dropdown-menu .hr-dropdown-search) {
  border-color: var(--neutral-200) !important;
  margin: 2px 4px 4px;
}

:deep(.hr-dropdown-menu .hr-dropdown-search input) {
  font-size: 12px;
}

/* Back link */
.project-dropdown-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  align-items: flex-start;
  border-radius: 4px;
  background: var(--neutral-50);
}

.project-dropdown-back:hover {
  color: var(--primary-600);
}

/* Option rows */
.project-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 6px;
  border-radius: 4px;
  transition: background 0.1s;
  cursor: pointer;
}

.project-option:hover {
  background: var(--neutral-100);
}

.project-option--active {
  background: var(--primary-200);
}

.project-option--active:hover {
  background: var(--primary-300);
}

.project-option-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.project-option-name {
  line-height: 1.3;
}

.project-option-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.project-option-lock {
  color: var(--neutral-500);
  flex-shrink: 0;
}

.project-switcher-name-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.project-switcher-lock {
  color: var(--neutral-500);
  flex-shrink: 0;
}

.project-option-desc {
  color: var(--neutral-700);
  margin-top: 0;
  line-height: 1.2;
}

/* Footer */
.project-dropdown-footer {
  padding: 10px;
  background: var(--neutral-100);
  border-radius: 4px;
}
</style>
